/**
 * Outfits a workflow step produced, drawn as spread cards rather than as JSON.
 *
 * Any output list whose items carry `garment_ids` (directly, or under
 * `outfit`) is a list of outfits: styling's ranked candidates, the accepted
 * set, a regenerated look. `styling_semantic_validate` gets one merged view of
 * every candidate it judged: its `all_results` in rank order, with what
 * `styling_combine_rank` scored each one and whether it was accepted, failed,
 * or kept as the fallback when every candidate failed. The JSON stays one
 * click away under the card's raw toggle.
 */

import type { ReactNode } from 'react';
import { OutfitSpreadCard } from './outfit-spread';
import type { StepCard } from './workflow-steps';

type Row = Record<string, unknown>;

export const SEMANTIC_VALIDATE_STEP = 'styling_semantic_validate';
export const COMBINE_RANK_STEP = 'styling_combine_rank';

export type OutfitCandidate = {
  key: string;
  garmentIds: string[];
  rank: number;
  status?: string | undefined;
  verdict?: 'accepted' | 'fallback' | 'failed' | 'passed' | undefined;
  reason?: string | undefined;
  compatibilityReason?: string | undefined;
  scores?: Record<string, number> | undefined;
};

export type OutfitList = {
  field: string;
  title: string;
  summary: string;
  candidates: OutfitCandidate[];
};

function record(value: unknown): Row | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : undefined;
}

function ids(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || !value.length) return undefined;
  const strings = value.filter((item): item is string => typeof item === 'string');
  return strings.length === value.length ? strings : undefined;
}

function outfitOf(item: unknown): Row | undefined {
  const row = record(item);
  if (!row) return undefined;
  if (ids(row.garment_ids)) return row;
  const nested = record(row.outfit);
  return nested && ids(nested.garment_ids) ? nested : undefined;
}

function scoresOf(value: unknown): Record<string, number> | undefined {
  const row = record(value);
  if (!row) return undefined;
  const numbers = Object.entries(row).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number',
  );
  return numbers.length ? Object.fromEntries(numbers) : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

const keyOf = (garmentIds: readonly string[]) => [...garmentIds].sort().join(',');

function genericList(field: string, value: unknown): OutfitList | undefined {
  if (!Array.isArray(value) || !value.length) return undefined;
  const outfits = value.map(outfitOf);
  if (!outfits.every(Boolean)) return undefined;
  const candidates = outfits.map((outfit, index) => {
    const row = outfit as Row;
    const garmentIds = ids(row.garment_ids) as string[];
    const item = record(value[index]) ?? {};
    return {
      key: `${field}-${index}-${keyOf(garmentIds)}`,
      garmentIds,
      rank: index + 1,
      status: text(record(item.validation)?.status) ?? text(item.status),
      reason: text(record(item.validation)?.reason) ?? text(item.reason),
      compatibilityReason: text(row.compatibility_reason),
      scores: scoresOf(row.scores),
    };
  });
  return {
    field,
    title: field.replaceAll('_', ' '),
    summary: `${candidates.length} ${candidates.length === 1 ? 'outfit' : 'outfits'}, in the order the step returned them.`,
    candidates,
  };
}

/** Every candidate `styling_semantic_validate` judged, joined to its ranking. */
function semanticValidateList(outputs: Row, siblings: readonly StepCard[]): OutfitList | undefined {
  const results = Array.isArray(outputs.all_results) ? outputs.all_results : [];
  const accepted = Array.isArray(outputs.accepted) ? outputs.accepted : [];
  const acceptedRows = accepted.map(outfitOf).filter((row): row is Row => Boolean(row));
  const acceptedKeys = new Set(acceptedRows.map((row) => keyOf(ids(row.garment_ids) ?? [])));
  const fallback = accepted.some(
    (item) => record(record(item)?.validation)?.forced_fallback === true,
  );
  const ranking = [...siblings]
    .reverse()
    .find((card) => card.step === COMBINE_RANK_STEP && card.outputsRecorded);
  const ranked = new Map<string, Row>();
  const rankedOutfits = record(ranking?.outputs)?.outfits;
  if (Array.isArray(rankedOutfits)) {
    for (const item of rankedOutfits) {
      const row = outfitOf(item);
      if (row) ranked.set(keyOf(ids(row.garment_ids) ?? []), row);
    }
  }
  // Older runs recorded only the accepted outfits; show those rather than nothing.
  const judged = results.length ? results : accepted;
  const candidates: OutfitCandidate[] = [];
  judged.forEach((item, index) => {
    const row = record(item);
    const outfit = outfitOf(item);
    const garmentIds = outfit ? (ids(outfit.garment_ids) as string[]) : undefined;
    if (!row || !garmentIds) return;
    const key = keyOf(garmentIds);
    const validation = record(row.validation) ?? row;
    const status = text(validation.status);
    const isAccepted = acceptedKeys.has(key);
    const rankedRow = ranked.get(key);
    candidates.push({
      key: `${index}-${key}`,
      garmentIds,
      rank: index + 1,
      status,
      verdict: isAccepted
        ? fallback
          ? 'fallback'
          : 'accepted'
        : status === 'FAIL'
          ? 'failed'
          : 'passed',
      reason: text(validation.reason),
      compatibilityReason: text(rankedRow?.compatibility_reason ?? outfit?.compatibility_reason),
      scores: scoresOf(rankedRow?.scores ?? outfit?.scores),
    });
  });
  if (!candidates.length) return undefined;
  const acceptedCount = candidates.filter(
    (candidate) => candidate.verdict === 'accepted' || candidate.verdict === 'fallback',
  ).length;
  const failed = candidates.filter((candidate) => candidate.verdict === 'failed').length;
  return {
    field: 'all_results',
    title: 'Every candidate',
    summary: fallback
      ? `All ${candidates.length} candidates failed validation, so the best-ranked one was kept as a fallback.`
      : `${candidates.length} candidates in rank order: ${acceptedCount} accepted, ${failed} failed validation${
          candidates.length - acceptedCount - failed > 0
            ? `, ${candidates.length - acceptedCount - failed} passed but ranked below the cut`
            : ''
        }.`,
    candidates,
  };
}

/**
 * The outfit lists in a step's outputs, and the output fields they replace in
 * the plain field list (so nothing is shown twice).
 */
export function stepOutfitLists(
  card: StepCard,
  siblings: readonly StepCard[] = [],
): { lists: OutfitList[]; consumed: Set<string> } {
  const outputs = record(card.outputs);
  const consumed = new Set<string>();
  if (!card.outputsRecorded || !outputs) return { lists: [], consumed };
  if (card.step === SEMANTIC_VALIDATE_STEP) {
    const list = semanticValidateList(outputs, siblings);
    if (list) {
      consumed.add('all_results');
      consumed.add('accepted');
      return { lists: [list], consumed };
    }
  }
  const lists: OutfitList[] = [];
  for (const [field, value] of Object.entries(outputs)) {
    const list = genericList(field, value);
    if (list) {
      lists.push(list);
      consumed.add(field);
    }
  }
  return { lists, consumed };
}

const VERDICT: Record<NonNullable<OutfitCandidate['verdict']>, { label: string; tone: string }> = {
  accepted: { label: 'Accepted', tone: 'bg-success-primary text-success-primary' },
  fallback: { label: 'Kept as fallback', tone: 'bg-warning-primary text-warning-primary' },
  failed: { label: 'Failed validation', tone: 'bg-error-primary text-error-primary' },
  passed: { label: 'Passed, not chosen', tone: 'bg-secondary text-secondary' },
};

function Pill({ children, tone }: { children: ReactNode; tone: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium shadow-xs ${tone}`}>
      {children}
    </span>
  );
}

function ScoreDetails({ scores }: { scores: Record<string, number> }) {
  const { final_score: finalScore, ...parts } = scores;
  return (
    <div className="flex flex-col gap-1 text-xs text-tertiary">
      {typeof finalScore === 'number' ? (
        <span>
          Final score <span className="font-mono text-secondary">{finalScore.toFixed(3)}</span>
        </span>
      ) : null}
      {Object.keys(parts).length ? (
        <details>
          <summary className="cursor-pointer">Score breakdown</summary>
          <dl className="mt-1 grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
            {Object.entries(parts).map(([name, value]) => (
              <div key={name} className="contents">
                <dt>{name.replaceAll('_', ' ')}</dt>
                <dd className="m-0 text-right font-mono text-secondary">{value.toFixed(3)}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}

export function OutfitListView({
  list,
  memberId,
}: {
  list: OutfitList;
  memberId?: string | undefined;
}) {
  return (
    <section
      aria-label={list.title}
      data-outfit-list={list.field}
      className="flex flex-col gap-2 md:col-span-2"
    >
      <h4 className="text-xs font-semibold tracking-wide text-tertiary uppercase">{list.title}</h4>
      <p className="m-0 text-xs text-tertiary">{list.summary}</p>
      <ol className="m-0 grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 xl:grid-cols-4">
        {list.candidates.map((candidate) => (
          <li key={candidate.key} data-outfit-candidate={candidate.rank}>
            <OutfitSpreadCard
              garments={candidate.garmentIds.map((id) => ({ id }))}
              memberId={memberId}
              title={`#${candidate.rank}`}
              badge={
                candidate.verdict ? (
                  <Pill tone={VERDICT[candidate.verdict].tone}>
                    {VERDICT[candidate.verdict].label}
                  </Pill>
                ) : candidate.status ? (
                  <Pill tone="bg-secondary text-secondary">{candidate.status}</Pill>
                ) : undefined
              }
            >
              {candidate.reason ? (
                <span className="text-xs text-secondary">{candidate.reason}</span>
              ) : null}
              {candidate.compatibilityReason ? (
                <span className="text-xs text-tertiary">{candidate.compatibilityReason}</span>
              ) : null}
              {candidate.scores ? <ScoreDetails scores={candidate.scores} /> : null}
            </OutfitSpreadCard>
          </li>
        ))}
      </ol>
    </section>
  );
}
